import { useTranslation } from 'react-i18next';
import { Store, UserRound } from 'lucide-react';
import type { CustomerSummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { FormField } from '@/components/ui/FormField';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';

/**
 * Who the order is for and where it is taken, always in view.
 *
 * The branch is the one the user is working in. Without one — an owner who has not chosen — the builder asks for it and picks nothing on its own: stock leaves the branch chosen here.
 */
export function OrderContext({ customer }: { customer: CustomerSummary }) {
  const { t } = useTranslation();
  const { branches, activeBranch, selectBranch } = useBranch();

  return (
    <div className="border-line flex flex-col gap-3 rounded-xl border bg-white px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6">
      <p className="flex min-w-0 items-center gap-2 text-sm">
        <UserRound className="text-muted size-4 shrink-0" aria-hidden />
        <span className="text-muted">{t('opticalOrders.builder.customer')}</span>
        <span dir="auto" className="text-ink min-w-0 truncate font-medium">
          {customer.displayName}
        </span>
        <span className="text-muted shrink-0 font-mono text-xs whitespace-nowrap" dir="ltr">
          {customer.customerNumber}
        </span>
      </p>

      {activeBranch ? (
        <p className="flex min-w-0 items-center gap-2 text-sm">
          <Store className="text-muted size-4 shrink-0" aria-hidden />
          <span className="text-muted">{t('opticalOrders.builder.branch')}</span>
          <span dir="auto" className="text-ink min-w-0 truncate font-medium">
            {activeBranch.name}
          </span>
          <span className="text-muted shrink-0 font-mono text-xs whitespace-nowrap" dir="ltr">
            {activeBranch.code}
          </span>
        </p>
      ) : (
        <div className="w-full space-y-2">
          <Alert tone="warning">{t('opticalOrders.builder.noBranch')}</Alert>
          {branches.length > 0 ? (
            <FormField label={t('opticalOrders.builder.chooseBranch')} required>
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value=""
                  placeholder={t('opticalOrders.builder.chooseBranchPlaceholder')}
                  options={branches.map((branch) => ({
                    value: branch.id,
                    label: `${branch.name} (${branch.code})`,
                  }))}
                  onChange={(event) => selectBranch(event.target.value)}
                />
              )}
            </FormField>
          ) : null}
        </div>
      )}
    </div>
  );
}
